import WarningIcon from "@mui/icons-material/Warning";
import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useContext, useEffect, useMemo, useState } from "react";

import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import FormInput from "../common/FormInput";
import { nameof } from "../common/ObjectUtils";
import VehicleEntranceService from "./service/VehicleEntranceService";
import { IExternalDDTFormInputs } from "./types";
import { formatTypeString } from "./utils";

const VehicleEntranceExternalDDT = (props: any) => {
  const { tenant } = useContext(ContextOperation);

  const navigate = useNavigate();
  const location = useLocation();
  const t = useOperationTranslation();

  props.changeSubtitle(t("vehicleEntrance.subtitles.external"));

  const { i18n } = useTranslation();
  const vehicleData = location.state as any;

  const form = useForm<IExternalDDTFormInputs>({
    defaultValues: {
      position: vehicleData?.locationStart?.value || "",
      position2: vehicleData?.locationEnd?.value || "",
      position3: vehicleData?.effectiveEndLocation?.key || "",
      startDate: vehicleData?.startDate || "",
      endDate: vehicleData?.endDate || "",
      expectedEndDate: vehicleData?.expectedEndDate || "",
    },
  });

  const { userLocations } = useContext(ContextOperation);

  const [allLocations, setAllLocations] = useState<any>([]);
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [statusReasonsDDT, setStatusReasonsDDT] = useState<AutoCompleteOption[]>([])

  useEffect(() => {
    if (vehicleData) {
      setAllLocations(
        vehicleData?.allLocations.map((item: any) => {
          return {
            ...item,
            label: `${item.locationEntityDesc} (${formatTypeString(item.type, t)})`,
            value: item.value,
          };
        })
      );
    }
  }, [i18n?.language]);

  useEffect(() => {
    VehicleEntranceService.get.statusReasonsDDT(tenant).then(
      response => {
        setStatusReasonsDDT(response.map(element => ({
          label: `${element.descr} - ${element.code}`,
          value: element.code,
        })))
      }
    )
  }, []);

  const onSubmit = (data: IExternalDDTFormInputs) => {
    const effectiveEndLoc = userLocations.filter(
      (item: any) => item.bpCode === form.watch("position3")
    )[0] || null;
    console.log("effective", effectiveEndLoc)

    navigate(`/operation/transfer/vehiclesEntrance/externalDDT/confirm`, {
      state: {
        tenant: vehicleData.tenant,
        searchResponse: {
          ...vehicleData,
        },
        deliveryNoteNumber: vehicleData.deliveryNoteNumber,
        allLocations: vehicleData.allLocations,
        lms: form.watch("position3"),
        locationStart:
          vehicleData.allLocations.filter(
            (item: any) => item.value === form.watch("position")
          )[0] || null,
        locationEnd:
          vehicleData.allLocations.filter(
            (item: any) => item.value === form.watch("position2")
          )[0] || null,
        effectiveEndLocation:
          userLocations.filter(
            (item: any) => item.key === form.watch("position3")
          )[0] || null,
        startDate: dayjs(form.watch("startDate")).toISOString(),
        endDate: dayjs(form.watch("endDate")).toISOString(),
        expectedEndDate: dayjs(form.watch("expectedEndDate")).toISOString(),
        reasonCode: "TFWORK",
      },
    });
    form.reset();
  };

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return userLocations.map((loc) => ({
      label: loc.descr || "",
      value: loc.key || "",
    })) || [];
  }, [userLocations]);

  return (
    <Mui.Box padding={{ md: 2 }}>
      <Mui.Paper>
        <Mui.Grid>
          <Mui.Grid>
            <Mui.Card>
              <Mui.CardContent>
              <Mui.Grid container alignItems={"center"}>
                  <Mui.Grid item xs={2}>
                    <Mui.IconButton onClick={() => navigate("/operation/transfer/vehiclesEntrance")}>
                        <MuiIcons.ArrowBack />
                    </Mui.IconButton>
                  </Mui.Grid>
                  <Mui.Grid item xs={8}>
                    <Mui.Typography
                      variant="h5"
                      whiteSpace="pre-line"
                      textAlign="center"
                      p={2}
                    >
                      {t("vehicleEntrance.withoutPlate.confirm.headerTitle")}
                    </Mui.Typography>
                  </Mui.Grid>
                </Mui.Grid>
                <Mui.Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                  }}
                  pt="20px"
                >
                  <Mui.Typography variant="body1">
                    {t("vehicleEntrance.withoutPlate.confirm.referenceTitle")}:
                  </Mui.Typography>
                  <Mui.Typography variant="h5">
                    {vehicleData?.ddt || vehicleData?.searchResponse?.ddt || ""}
                  </Mui.Typography>
                </Mui.Box>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <Mui.Box sx={{ mt: 4 }}>
                    <AutoComplete
                      fullWidth
                      options={allLocations}
                      label={t("vehicleEntrance.withoutPlate.confirm.startPoint")}
                      {...form.register("position", {
                        required: t("vehicleExit.status.toErrorMessage"),
                      })}
                      value={form.watch("position") || ""}
                      onChange={(newStatusValue) => {
                        console.log(newStatusValue)
                        form.setValue("position", newStatusValue as string);
                        console.log(form.watch("position"))
                      }}
                      error={form.formState.errors?.position?.message?.toString()}
                    />
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3 }}>
                    <DateTimePicker
                      ampm={false}
                      maxDateTime={dayjs().local()}
                      renderInput={(props) => (
                        <Mui.TextField
                          {...props}
                          fullWidth
                          error={!!form.formState.errors.startDate}
                          helperText={form.formState.errors.startDate?.message?.toString()}
                          onReset={(event) => form.setValue("startDate", null)}
                        />
                      )}
                      inputFormat={"DD/MM/YYYY HH:mm"}
                      label={t(
                        "vehicleEntrance.withoutPlate.confirm.startDate"
                      )}
                      value={form.watch("startDate")}
                      {...form.register("startDate", {
                        validate: (value) => {
                          const dateSelected = dayjs(value);
                          if (value && !dateSelected.isValid()) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.startDateInvalid"
                            );
                          }
                          if (
                            dateSelected.isAfter(
                              dayjs().local()
                            )
                          ) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.startDateAfterNow"
                            );
                          }
                        },
                      })}
                      onChange={(newDateValue) => {
                        // @ts-ignore
                        form.setValue("startDate", newDateValue);
                      }}
                    />
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3 }}>
                    <AutoComplete
                      fullWidth
                      options={locations}
                      label={t("vehicleEntrance.withoutPlate.confirm.endPoint")}
                      {...form.register("position2", {
                        required: t("vehicleExit.status.toErrorMessage"),
                      })}
                      value={form.watch("position2") || ""}
                      onChange={(newStatusValue) => {
                        if (
                          form.watch("position3") !== "" &&
                          form.watch("position3") !== newStatusValue
                        ) {
                          setDialogVisible(true);
                        }
                        form.setValue("position2", newStatusValue as string);
                      }}
                      error={form.formState.errors?.position2?.message?.toString()}
                    />
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3 }}>
                    <DateTimePicker
                      ampm={false}
                      minDateTime={dayjs(form.getValues("startDate"))}
                      maxDateTime={dayjs().local()}
                      renderInput={(props) => (
                        <Mui.TextField
                          {...props}
                          fullWidth
                          error={!!form.formState.errors.expectedEndDate}
                          helperText={form.formState.errors.expectedEndDate?.message?.toString()}
                          onReset={(event) => form.setValue("expectedEndDate", null)}
                        />
                      )}
                      inputFormat={"DD/MM/YYYY HH:mm"}
                      label={t(
                        "vehicleEntrance.withoutPlate.confirm.expectedEndDate"
                      )}
                      value={form.watch("expectedEndDate")}
                      {...form.register("expectedEndDate", {
                        validate: (value) => {
                          const dateSelected = dayjs(value);
                          if (value && !dateSelected.isValid()) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateInvalid"
                            );
                          }
                          if (
                            dateSelected.isBefore(
                              dayjs(form.watch("startDate"))
                            )
                          ) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateBeforeStart"
                            );
                          }
                          if (
                            dateSelected.isAfter(
                              dayjs().local()
                            )
                          ) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateAfterNow"
                            );
                          }
                        },
                      })}
                      onChange={(newDateValue) => {
                        // @ts-ignore
                        form.setValue("expectedEndDate", newDateValue);
                      }}
                    />
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3 }}>
                    <AutoComplete
                      fullWidth
                      options={locations}
                      label={t("vehicleEntrance.withoutPlate.confirm.lmsLabel")}
                      {...form.register("position3", {
                        required: t("vehicleExit.status.toErrorMessage"),
                      })}
                      value={form.watch("position3") || ""}
                      onChange={(newStatusValue) => {
                        if (
                          form.watch("position2") !== "" &&
                          form.watch("position2") !== newStatusValue
                        ) {
                          setDialogVisible(true);
                        }
                        console.log(newStatusValue)
                        form.setValue("position3", newStatusValue as string);
                        console.log(form.watch("position3"))
                      }}
                      error={Boolean(
                        form.formState.errors?.position3?.message?.toString()
                      )}
                    />
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3 }}>
                    <DateTimePicker
                      ampm={false}
                      minDateTime={dayjs(form.getValues("startDate"))}
                      maxDateTime={dayjs().local()}
                      renderInput={(props) => (
                        <Mui.TextField
                          {...props}
                          fullWidth
                          error={!!form.formState.errors.endDate}
                          helperText={form.formState.errors.endDate?.message?.toString()}
                          onReset={(event) =>
                            form.setValue("endDate", null)
                          }
                        />
                      )}
                      inputFormat={"DD/MM/YYYY HH:mm"}
                      label={t(
                        "vehicleEntrance.withoutPlate.confirm.realArrivalDate"
                      )}
                      value={form.watch("endDate")}
                      {...form.register("endDate", {
                        validate: (value) => {
                          const dateSelected = dayjs(value);
                          if (value && !dateSelected.isValid()) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateInvalid"
                            );
                          }
                          if (
                            dateSelected.isBefore(
                              dayjs(form.watch("startDate"))
                            )
                          ) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateBeforeStart"
                            );
                          }
                          if (
                            dateSelected.isAfter(
                              dayjs().local()
                            )
                          ) {
                            return t(
                              "vehicleAvailability.status.newBlock.inputs.endDateAfterNow"
                            );
                          }
                        },
                      })}
                      onChange={(newDateValue) => {
                        // @ts-ignore
                        form.setValue("endDate", newDateValue);
                      }}
                    />
                  </Mui.Box>
                  
                  <Mui.Button
                    fullWidth
                    variant="contained"
                    style={{
                      color: "white",
                      backgroundColor: "#d12c93",
                      marginTop: 20,
                    }}
                    type="submit"
                  >
                    {t("vehicleEntrance.withoutPlate.confirm.buttonLabel")}
                  </Mui.Button>
                </form>
              </Mui.CardContent>
            </Mui.Card>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.Paper>

      <Mui.Dialog open={isDialogVisible}>
        <Mui.DialogTitle>
          <WarningIcon sx={{ color: "orange", fontSize: "20px" }} />
        </Mui.DialogTitle>

        <Mui.DialogContent>
          {" "}
          {t("vehicleEntrance.externalDDT.diffrentRealLocation")}
        </Mui.DialogContent>

        <Mui.DialogActions>
          <Mui.Button
            variant="contained"
            onClick={() => setDialogVisible(false)}
          >
            OK
          </Mui.Button>
        </Mui.DialogActions>
      </Mui.Dialog>
    </Mui.Box>
  );
};
export default VehicleEntranceExternalDDT;
